import { useMutation, useQuery } from '@apollo/client'
import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  CREATE_EMPLOYEE,
  GET_EMPLOYEE_BY_ID,
  UPDATE_EMPLOYEE,
} from '../../../../graphql/Catalog/Employees/employees'
import { toastSweetAlert } from '../../../Helpers/ToastSweetAlert'
import * as Yup from 'yup'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import InputController from '../../../Global/InputController'
import Box from '../../../Global/Box'
import { useLoading } from '../../../../LoadingOverlay/LoadingOverlayContext'
import useBackPage from '../../../../CustomHooks/BackPage/useBackPage'
import { GET_ALL_JOB_POSITIONS } from '../../../../graphql/Catalog/JobPositions/jobPositions'
import { GET_ALL_SCHEDULE_GROUPS } from '../../../../graphql/Catalog/Employees/Groups/scheduleGroups'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import ContentHeader from '../../../Layout/ContentHeader'

const EmployeesNew = () => {
  const history = useHistory()
  const { id: _id, show } = useParams()

  const { showLoading, hideLoading } = useLoading()
  const backPage = useBackPage()

  const [files, setFiles] = useState({
    photo_url: null,
    documents: [],
    documentsIdsOriginal: [],
  })
  const [options, setOptions] = useState({
    areas: [],
    jobPositions: [],
    scheduleGroups: [],
  })

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required('El nombre es requerido'),
    last_name: Yup.string().required('El apellido es requerido'),
    email: Yup.string()
      .email('El correo electrónico debe ser válido')
      .required('El correo electrónico es requerido'),
    phone: Yup.string().required('El teléfono es requerido'),
    area_id: Yup.number().required('El área es requerida'),
    job_position_id: Yup.number().required('La posición laboral es requerida'),
    schedule_group_id: Yup.number().required(
      'El grupo de horario es requerido'
    ),
    photo_url: Yup.string().required('La foto del empleado es requerida'),
    documents: Yup.array().nullable(),
  })

  const {
    reset,
    formState: { errors },
    handleSubmit,
    control,
    methods,
    getValues,
  } = useForm({
    resolver: yupResolver(validationSchema),
  })

  const {
    loading: loadingEmployees,
    error: errorEmployees,
    data: dataEmployees,
  } = useQuery(GET_EMPLOYEE_BY_ID, {
    variables: {
      getEmployeeByIdId: _id ? parseInt(_id) : null,
    },
    skip: !_id,
    fetchPolicy: 'cache-and-network',
  })

  const {
    data: dataJob,
    loading: loadingJob,
    error: errorJob,
  } = useQuery(GET_ALL_JOB_POSITIONS, {
    variables: {},
    fetchPolicy: 'cache-and-network',
  })

  const {
    data: dataScheduleGroups,
    loading: loadingScheduleGroups,
    error: errorScheduleGroups,
  } = useQuery(GET_ALL_SCHEDULE_GROUPS, {
    variables: {},
    fetchPolicy: 'cache-and-network',
  })

  const [createEmployee] = useMutation(CREATE_EMPLOYEE)
  const [updateEmployee] = useMutation(UPDATE_EMPLOYEE)

  useEffect(() => {
    try {
      if (!_id) return
      if (loadingEmployees) return
      if (errorEmployees) throw new Error(errorEmployees.message)
      let documentsIds = []
      const employeeData = dataEmployees.getEmployeeById
      reset({
        first_name: employeeData.first_name,
        last_name: employeeData.last_name,
        email: employeeData.email,
        phone: employeeData.phone,
        area_id: employeeData.area_id,
        job_position_id: employeeData.job_position_id,
        schedule_group_id: employeeData.schedule_group_id,
        photo_url: employeeData.photo_url || '',
      })

      const documents = employeeData.EmployeeToDocuments.map((doc) => {
        documentsIds.push(doc.id)
        return {
          id: doc.id,
          src: doc.file_url,
          name: doc.name,
          type: `image/jpg`,
        }
      })

      setFiles({
        photo_url: employeeData.photo_url ? employeeData.photo_url : null,
        documents: documents.length > 0 ? documents : [],
        documentsIdsOriginal: documentsIds,
      })
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    }
  }, [loadingEmployees, errorEmployees, dataEmployees])

  useEffect(() => {
    try {
      if (loadingJob) return
      if (errorJob) throw new Error(errorJob.message)
      const jobPositions = dataJob.getAllJobPositions.rows.map((job) => {
        return {
          value: job.id,
          label: job.name,
        }
      })

      setOptions((prev) => ({
        ...prev,
        jobPositions: jobPositions,
      }))
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    }
  }, [dataJob, loadingJob, errorJob])

  useEffect(() => {
    try {
      if (loadingScheduleGroups) return
      if (errorScheduleGroups) throw new Error(errorScheduleGroups.message)
      const scheduleGroups =
        dataScheduleGroups.getAllScheduleGroupsEmployees.rows.map((group) => {
          return {
            value: group.id,
            label: group.name,
          }
        })
      setOptions((prev) => ({
        ...prev,
        scheduleGroups: scheduleGroups,
      }))
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    }
  }, [dataScheduleGroups, loadingScheduleGroups, errorScheduleGroups])

  const onSubmit = async (data) => {
    showLoading()
    try {
      if (_id) {
        // If editing, we need to check if there are documents to delete
        if (data.documents && data.documents.length > 0) {
          const documentsToDelete = files.documentsIdsOriginal.filter(
            (docId) => !data.documents.some((doc) => doc.id === docId)
          )
          data.documentsToDelete =
            documentsToDelete.length > 0 ? documentsToDelete : null

          const documents = data.documents.map((doc) => {
            if (!doc.id) {
              return {
                id: null,
                name: doc.name,
                file: doc,
              }
            }
          })

          //Filter out undefined values
          data.documents = documents.filter(
            (doc) => doc !== undefined && doc !== null
          )
        }

        const { data: response } = await updateEmployee({
          variables: {
            updateEmployeeId: +_id,
            input: {
              first_name: data.first_name,
              last_name: data.last_name,
              email: data.email,
              phone: data.phone,
              area_id: data.area_id,
              job_position_id: data.job_position_id,
              schedule_group_id: data.schedule_group_id,
              photo_url:
                typeof getValues('photo_url') === 'object'
                  ? getValues('photo_url')
                  : null,
              documents: data.documents,
              documentsToDelete: data.documentsToDelete || [],
            },
          },
        })

        if (response.updateEmployee) {
          return toastSweetAlert(
            {
              mode: 'ok',
              message: 'Empleado actualizado correctamente',
            },
            history.push(backPage)
          )
        }
      }
      const { data: response } = await createEmployee({
        variables: {
          input: {
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            phone: data.phone,
            area_id: data.area_id,
            job_position_id: data.job_position_id,
            schedule_group_id: data.schedule_group_id,
            photo_url: getValues('photo_url'),
            documents: files.documents.map((doc) => ({
              id: doc.id || null,
              name: doc.name,
              file: doc || null,
            })),
          },
        },
      })

      if (response.createEmployee) {
        return toastSweetAlert(
          {
            mode: 'ok',
            message: _id
              ? 'Empleado actualizado correctamente'
              : 'Empleado creado correctamente',
          },
          history.push(backPage)
        )
      }
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    } finally {
      hideLoading()
      // reset({
      //   first_name: '',
      //   last_name: '',
      //   email: '',
      //   phone: '',
      //   area_id: null,
      //   job_position_id: null,
      //   schedule_group_id: null,
      //   photo_url: '',
      // })
      // setFiles({
      //   photo_url: null,
      //   documents: [],
      //   documentsIdsOriginal: [],
      // })
    }
  }

  return (
    <>
      <ContentHeader
        title=""
        breadcrumb=""
        windowTitle={
          _id
            ? show
              ? 'Detalles del Empleado'
              : 'Editar Empleado'
            : 'Nuevo Empleado'
        }
      />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            title={
              _id
                ? show
                  ? 'Detalles del Empleado'
                  : 'Editar Empleado'
                : 'Nuevo Empleado'
            }
            btnSubmit={!show}
            btnSubmitText={_id ? 'Actualizar Empleado' : 'Crear Empleado'}
            BackPageHeader
            content={
              <>
                {show ? (
                  <>
                    {dataEmployees && (
                      <div className="row">
                        <div className="mb-3 col-lg-12">
                          <InputController
                            label="Foto del Empleado"
                            name="photo_url"
                            inputType="image"
                            previewPic={
                              files.photo_url ? files.photo_url : null
                            }
                            validateFormat="imageAvatar"
                            validateFormatMsg="El formato de la foto debe ser una imagen válida (jpg, jpeg, png)"
                            disabled={show}
                            control={control}
                          />
                        </div>
                        <div className="col-lg-4 mt-2">
                          <div className="card h-100 shadow-sm">
                            <div className="card-body">
                              <h5 className="card-title text-secondary">
                                <i className="fas fa-user mr-2"></i>
                                <strong>Nombre Completo</strong>
                              </h5>
                              <p className="card-text lead">
                                {dataEmployees.getEmployeeById.first_name}{' '}
                                {dataEmployees.getEmployeeById.last_name}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-4 mt-2">
                          <div className="card h-100 shadow-sm">
                            <div className="card-body">
                              <h5 className="card-title text-secondary">
                                <i className="fas fa-envelope mr-2"></i>
                                <strong>Correo Electrónico</strong>
                              </h5>
                              <p className="card-text lead">
                                {dataEmployees.getEmployeeById.email}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-4 mt-2">
                          <div className="card h-100 shadow-sm">
                            <div className="card-body">
                              <h5 className="card-title text-secondary">
                                <i className="fas fa-phone mr-2"></i>
                                <strong>Teléfono</strong>
                              </h5>
                              <p className="card-text lead">
                                {dataEmployees.getEmployeeById.phone}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-4 mt-2">
                          <div className="card h-100 shadow-sm">
                            <div className="card-body">
                              <h5 className="card-title text-secondary">
                                <i className="fas fa-building mr-2"></i>
                                <strong>Área</strong>
                              </h5>
                              <p className="card-text lead">
                                {dataEmployees.getEmployeeById.area_id}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-4 mt-2">
                          <div className="card h-100 shadow-sm">
                            <div className="card-body">
                              <h5 className="card-title text-secondary">
                                <i className="fas fa-briefcase mr-2"></i>
                                <strong>Posición Laboral</strong>
                              </h5>
                              <p className="card-text lead">
                                {dataEmployees.getEmployeeById
                                  .EmployeeToJobPosition
                                  ? dataEmployees.getEmployeeById
                                      .EmployeeToJobPosition.name
                                  : 'No asignado'}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-4 mt-2">
                          <div className="card h-100 shadow-sm">
                            <div className="card-body">
                              <h5 className="card-title text-secondary">
                                <i className="fas fa-clock mr-2"></i>
                                <strong>Grupo de Horario</strong>
                              </h5>
                              <p className="card-text lead">
                                {dataEmployees.getEmployeeById
                                  .EmployeeToScheduleGroup
                                  ? dataEmployees.getEmployeeById
                                      .EmployeeToScheduleGroup.name
                                  : 'No asignado'}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-12 mt-2">
                          <div className="card h-100 shadow-sm">
                            <div className="card-body">
                              <h5 className="card-title text-secondary">
                                <i className="fas fa-file mr-2"></i>
                                <strong>Documentos Adjuntos</strong>
                              </h5>
                              <p className="card-text lead">
                                {dataEmployees.getEmployeeById
                                  .EmployeeToDocuments.length > 0 ? (
                                  <>
                                    <ReportAttachments
                                      attachments={
                                        dataEmployees.getEmployeeById
                                          .EmployeeToDocuments
                                      }
                                    />
                                  </>
                                ) : (
                                  'No hay documentos adjuntos'
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="row">
                      <div className="mb-3 col-lg-12">
                        <InputController
                          label="Foto del Empleado"
                          name="photo_url"
                          inputType="image"
                          previewPic={files.photo_url ? files.photo_url : null}
                          validateFormat="imageAvatar"
                          validateFormatMsg="El formato de la foto debe ser una imagen válida (jpg, jpeg, png)"
                          disabled={show}
                          control={control}
                        />
                      </div>
                      <div className="mb-3 col-lg-6">
                        <InputController
                          label="Nombre"
                          name="first_name"
                          placeholder="Ingrese el nombre del empleado"
                          control={control}
                        />
                      </div>
                      <div className="mb-3 col-lg-6">
                        <InputController
                          label="Apellido"
                          name="last_name"
                          placeholder="Ingrese el apellido del empleado"
                          control={control}
                        />
                      </div>
                      <div className="mb-3 col-lg-6">
                        <InputController
                          label="Correo Electrónico"
                          name="email"
                          placeholder="Ingrese el correo electrónico del empleado"
                          control={control}
                        />
                      </div>
                      <div className="mb-3 col-lg-6">
                        <InputController
                          label="Teléfono"
                          name="phone"
                          placeholder="Ingrese el teléfono del empleado"
                          control={control}
                        />
                      </div>
                      <div className="mb-3 col-lg-6">
                        <InputController
                          label="Área"
                          name="area_id"
                          placeholder="Seleccione el área del empleado"
                          control={control}
                        />
                      </div>
                      <div className="mb-3 col-lg-6">
                        <InputController
                          label="Posición Laboral"
                          name="job_position_id"
                          placeholder="Seleccione la posición laboral del empleado"
                          control={control}
                          inputType="choosen"
                          options={options.jobPositions}
                        />
                      </div>
                      <div className="mb-3 col-lg-6">
                        <InputController
                          label="Grupo de Horario"
                          name="schedule_group_id"
                          placeholder="Seleccione el grupo de horario del empleado"
                          control={control}
                          inputType="choosen"
                          options={options.scheduleGroups}
                        />
                      </div>
                      <div className="mb-3 col-lg-12">
                        <InputController
                          label="Documentos"
                          name="documents"
                          placeholder="Adjunte documentos del empleado"
                          control={control}
                          inputType="attachment"
                          maxFiles={5}
                          editDropAttachment={
                            files.documents.length > 0 && _id
                              ? files.documents
                              : false
                          }
                          disabled={show}
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            }
          />
        </form>
      </FormProvider>
    </>
  )
}

const ReportAttachments = ({ attachments }) => {
  const MySwal = withReactContent(Swal)

  // const [uniqueUser, setUniqueUser] = useState([])

  // useEffect(() => {
  //   if (attachments) {
  //     const uniqueUsers = attachments.reduce((acc, attachment) => {
  //       if (!acc.some((user) => user.user_id === attachment.user_id)) {
  //         acc.push({
  //           user_id: attachment.user_id,
  //           user: attachment.UserOfReportAttachment,
  //         })
  //       }
  //       return acc
  //     }, [])
  //     setUniqueUser(uniqueUsers)
  //   }
  // }, [attachments])

  const switchFile = (type_file) => {
    const data = {
      pdf: {
        icon: 'fas fa-file-pdf',
        text: 'PDF',
      },
      img: {
        icon: 'fas fa-image',
        text: 'IMG',
      },
    }
    return (
      <>
        {data[type_file ? type_file : 'img']?.text}
        <i
          className={`${data[type_file ? type_file : 'img'].icon} fa-2x ml-2`}
        />
      </>
    )
  }

  const handleModalImage = (url, id) => {
    MySwal.fire({
      title: (
        <>
          <div className="d-flex justify-content-center align-items-center">
            <i className="fas fa-image mr-2" />
            <span>Vista previa de la imagen {`IMG_${id}.jpg`}</span>
          </div>
          <div className="border"></div>
        </>
      ),
      // titleText: `IMG_${id}.jpg`,
      imageUrl: url,
      imageWidth: 500,
      imageHeight: 440,
      // imageAlt: "Custom image"
      // buttonsStyling: false,
      // confirmButtonText: 'Aceptar',
      showConfirmButton: false,
      showCloseButton: true,
      customClass: {
        title: 'StandarModalTitle',
        htmlContainer: 'StandarModalHtmlContainer',
        confirmButton: 'btn btn-orange',
        denyButton: 'btn StandarModalCancelButtonLogOut',
        footer: 'btn StandarModalFooterLogOut',
      },
    })
  }

  return (
    <Box
      withCard={false}
      title=""
      classNameHeader="card-header py-1"
      content={
        <>
          {attachments?.length === 0 ? (
            'No hay archivos adjuntos'
          ) : (
            <div className="d-flex flex-wrap row">
              {attachments.map((attachment, index) => {
                return (
                  <div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xl-6 mb-3">
                    <div key={attachment.id} className="btn btn-light">
                      {attachment?.type_file === 'img' ? (
                        <a
                          className="link-primary documentLink"
                          style={{ textDecoration: 'none' }}
                          as="button"
                          title="Descargar"
                          onClick={() =>
                            handleModalImage(attachment.url, attachment.id)
                          }
                        >
                          <img
                            src={attachment.url}
                            alt=""
                            width="80px"
                            height="80px"
                          />
                          {/* {switchFile(attachment?.type_file)} */}
                        </a>
                      ) : (
                        <a
                          className="link-primary documentLink"
                          style={{ textDecoration: 'none' }}
                          as="button"
                          href={attachment.url}
                          target="_blank"
                          rel="noreferrer"
                          data-toggle="tooltip"
                          data-placement="top"
                          title="Descargar"
                        >
                          {switchFile(attachment?.type_file)}
                        </a>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      }
    />
  )
}
export default EmployeesNew
